/**
 * Free PEP Readiness Check: the public lead magnet.
 *
 * Standalone page: no navigation chrome, no account, built to be finished on a phone in about
 * five minutes. Four steps: pick the grade, answer ten real questions from the published bank,
 * leave an email, read the marked report.
 *
 * The client never learns which answer was right. It posts the chosen option and gets progress
 * back, nothing else; the server grades, scores and builds the report.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Illustration } from "@/components/Illustration";
import { PepQuestion, type PepQuestionData } from "@/components/pepcheck/PepQuestion";
import { PepResult, type PepReport } from "@/components/pepcheck/PepResult";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { capturePostHogEvent } from "@/lib/posthog";
import {
  ArrowRight, CalendarDays, CheckCircle2, ClipboardList, Loader2, Lock, Timer,
} from "lucide-react";

interface CountdownData {
  days: number; date: string; dateLabel: string; subjects: string[]; isTheirSitting: boolean; headline: string;
}

type Step = "intro" | "quiz" | "capture" | "result";

const GRADES = [4, 5, 6] as const;

/** Query-string attribution, read once so a refresh mid-check does not lose it. */
function readAttribution() {
  if (typeof window === "undefined") return { source: null as string | null, utm: {} as Record<string, string> };
  const sp = new URLSearchParams(window.location.search);
  const utm: Record<string, string> = {};
  for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "ref"]) {
    const v = sp.get(key);
    if (v) utm[key] = v.slice(0, 200);
  }
  const source = sp.get("source") || utm.utm_source || (document.referrer ? "referral" : "direct");
  return { source, utm };
}

export default function PepCheck() {
  const [, navigate] = useLocation();
  const { toast } = useToast();

  const [step, setStep] = useState<Step>("intro");
  const [grade, setGrade] = useState<number | null>(null);
  const [countdown, setCountdown] = useState<CountdownData | null>(null);
  const [checkId, setCheckId] = useState<string | null>(null);
  const [questions, setQuestions] = useState<PepQuestionData[]>([]);
  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState<PepReport | null>(null);
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [school, setSchool] = useState("");
  const [loadError, setLoadError] = useState<string | null>(null);
  // True only while a report reopened from the email link is being fetched.
  const [resolving, setResolving] = useState(() =>
    typeof window !== "undefined" && !!new URLSearchParams(window.location.search).get("id"));

  const attribution = useMemo(readAttribution, []);
  const questionStartedAt = useRef<number>(Date.now());
  const topRef = useRef<HTMLDivElement>(null);

  // The countdown headline, and a report reopened from the email link (/pep-check?id=...).
  useEffect(() => {
    document.title = "Free PEP Readiness Check | EduFocal Academy";
    const existingId = new URLSearchParams(window.location.search).get("id");
    if (existingId) {
      setCheckId(existingId);
      fetch(`/api/pep-check/${encodeURIComponent(existingId)}/result`)
        .then(r => (r.ok ? r.json() : Promise.reject(new Error("not found"))))
        .then((data: PepReport) => { setReport(data); setGrade(data.grade); setStep("result"); })
        .catch(() => setLoadError("We could not find that report. You can take the check again below."))
        .finally(() => setResolving(false));
    }
    fetch("/api/pep-check/countdown")
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d?.countdown) setCountdown(d.countdown); })
      .catch(() => null);
  }, []);

  // Refresh the headline once a grade is chosen: Grade 6 sits the next paper, 4 and 5 do not.
  useEffect(() => {
    if (!grade) return;
    fetch(`/api/pep-check/countdown?grade=${grade}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d?.countdown) setCountdown(d.countdown); })
      .catch(() => null);
  }, [grade]);

  useEffect(() => {
    topRef.current?.scrollIntoView({ block: "start" });
  }, [index, step]);

  async function startCheck(chosenGrade: number) {
    setBusy(true);
    setLoadError(null);
    try {
      const res = await apiRequest("POST", "/api/pep-check/start", {
        grade: chosenGrade,
        source: attribution.source,
        utm: attribution.utm,
      });
      const data = await res.json();
      setCheckId(data.checkId);
      setQuestions(data.questions || []);
      setIndex(0);
      setSelected(null);
      questionStartedAt.current = Date.now();
      setStep("quiz");
      capturePostHogEvent("PEP Check Started", { grade: chosenGrade, source: attribution.source });
    } catch (error: any) {
      setLoadError(error?.message || "We could not start the check. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function submitAnswer() {
    if (!checkId || !selected) return;
    const question = questions[index];
    setBusy(true);
    try {
      await apiRequest("POST", `/api/pep-check/${checkId}/answer`, {
        questionId: question.id,
        answer: selected,
        ms: Date.now() - questionStartedAt.current,
      });
      if (index + 1 >= questions.length) {
        setStep("capture");
        capturePostHogEvent("PEP Check Completed", { grade });
      } else {
        setIndex(index + 1);
        setSelected(null);
        questionStartedAt.current = Date.now();
      }
    } catch (error: any) {
      toast({ title: "That did not save", description: error?.message || "Please try again.", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  }

  async function captureAndShow(e: React.FormEvent) {
    e.preventDefault();
    if (!checkId) return;
    setBusy(true);
    try {
      await apiRequest("POST", `/api/pep-check/${checkId}/capture`, {
        email: email.trim(),
        firstName: firstName.trim(),
        school: school.trim() || null,
      });
      const res = await fetch(`/api/pep-check/${checkId}/result`);
      if (!res.ok) throw new Error("Could not load your report");
      // The offer, its code and its per-parent deadline come from the server, which reads the
      // acquisition offers registry. Nothing about the offer is decided in the browser.
      const data: PepReport = await res.json();
      setReport(data);
      setStep("result");
      capturePostHogEvent("PEP Check Captured", { grade, band: data.band, score: data.score });
    } catch (error: any) {
      toast({ title: "We could not save that", description: error?.message || "Please check the details and try again.", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  }

  async function handleOfferClick(where: string) {
    if (!report) return;
    const target = where === "performance-task" ? "/performance-tasks" : report.offer.url;
    capturePostHogEvent("PEP Check Offer Clicked", { where, code: report.offer.code, band: report.band });
    try {
      await apiRequest("POST", `/api/pep-check/${report.id}/offer-click`, { code: report.offer.code });
    } catch {
      // A dead click log must never stop a parent reaching checkout.
    }
    // Registry offers are absolute links carrying a signed token, so they leave the SPA router.
    if (/^https?:\/\//.test(target)) window.location.href = target;
    else navigate(target);
  }

  const progress = questions.length ? Math.round(((index + (selected ? 1 : 0)) / questions.length) * 100) : 0;

  return (
    <div className="min-h-screen bg-background">
      <div ref={topRef} />

      {/* Chrome-free header: brand, and the progress bar once the check is running. */}
      <header className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between gap-3">
          <a href="/" className="flex items-center gap-2 min-w-0" aria-label="EduFocal Academy">
            <img src="/edufocal-academy-logo.png" alt="EduFocal Academy" className="h-7 w-auto" />
          </a>
          {step === "quiz" && (
            <span className="text-sm font-semibold tabular-nums text-muted-foreground flex-shrink-0">
              {index + 1} / {questions.length}
            </span>
          )}
        </div>
        {step === "quiz" && (
          <div className="h-1 bg-primary/15">
            <div className="h-1 bg-primary transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
        )}
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 pb-28 md:pb-10">
        {resolving && (
          <div className="py-24 text-center text-muted-foreground" data-testid="pep-resolving">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
            <p className="text-sm mt-3">Opening your report</p>
          </div>
        )}

        {/* ---------------------------------------------------------------- intro */}
        {!resolving && step === "intro" && (
          <div className="space-y-6">
            <div className="text-center">
              <Illustration name="owl-wave" size={132} />
              {countdown && (
                <p className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full bg-primary/15 text-primary mt-2">
                  <CalendarDays className="w-3.5 h-3.5" />
                  {countdown.subjects.join(" and ")} on {countdown.dateLabel}
                </p>
              )}
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight mt-3">
                {countdown ? `PEP is in ${countdown.days} days.` : "PEP is coming."}
                <br />
                <span className="text-primary">Is your child ready?</span>
              </h1>
              <p className="text-base text-muted-foreground mt-3 leading-relaxed">
                Ten real PEP questions, straight from the bank Jamaican students practise with every day. You get a
                marked report with the band, the strongest subject and the two topics to fix first.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-2 text-center">
              {[
                { icon: ClipboardList, label: "10 questions" },
                { icon: Timer, label: "About 5 minutes" },
                { icon: Lock, label: "No account" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="rounded-xl border border-border bg-card p-3">
                  <Icon className="w-4 h-4 text-primary mx-auto" />
                  <p className="text-xs font-medium mt-1.5">{label}</p>
                </div>
              ))}
            </div>

            <div className="rounded-xl border border-border bg-card p-4 md:p-5 shadow-sm">
              <h2 className="text-base md:text-lg font-semibold">Which grade is your child in?</h2>
              <div className="grid grid-cols-3 gap-2 mt-3">
                {GRADES.map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setGrade(g)}
                    data-testid={`button-grade-${g}`}
                    className={`rounded-xl border-2 py-4 transition-colors ${
                      grade === g ? "border-primary bg-primary/10" : "border-border bg-background hover:border-primary/50"
                    }`}
                  >
                    <span className="block text-xs font-medium text-muted-foreground">Grade</span>
                    <span className="block text-2xl font-bold tabular-nums">{g}</span>
                  </button>
                ))}
              </div>
              {grade && countdown && !countdown.isTheirSitting && (
                <p className="text-xs text-muted-foreground mt-3">
                  Grade {grade} does not sit this paper. The countdown is to the next national sitting, and the work
                  that gets a child ready starts long before their own year.
                </p>
              )}
              <Button
                size="lg"
                className="w-full h-12 mt-4 text-base font-semibold"
                disabled={!grade || busy}
                onClick={() => grade && startCheck(grade)}
                data-testid="button-start-check"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Start the free check
                {!busy && <ArrowRight className="w-4 h-4 ml-2" />}
              </Button>
              {loadError && <p className="text-sm text-destructive mt-3">{loadError}</p>}
            </div>

            <p className="text-xs text-center text-muted-foreground">
              Questions come from the published EduFocal Academy bank. We never show your child the answers during
              the check, so the score means something.
            </p>
          </div>
        )}

        {/* ---------------------------------------------------------------- quiz */}
        {step === "quiz" && questions[index] && (
          <>
            <PepQuestion
              question={questions[index]}
              index={index}
              total={questions.length}
              selected={selected}
              onSelect={setSelected}
            />
            {/* Sticky on a phone so the child never has to hunt for Next. */}
            <div className="fixed md:static bottom-0 left-0 right-0 z-20 bg-background/95 backdrop-blur border-t md:border-0 border-border p-4 md:p-0 md:mt-5">
              <div className="max-w-2xl mx-auto">
                <Button
                  size="lg"
                  className="w-full h-12 text-base font-semibold"
                  disabled={!selected || busy}
                  onClick={submitAnswer}
                  data-testid="button-next-question"
                >
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  {index + 1 >= questions.length ? "Finish and see the report" : "Next question"}
                  {!busy && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>
              </div>
            </div>
          </>
        )}

        {/* ---------------------------------------------------------------- capture */}
        {step === "capture" && (
          <form onSubmit={captureAndShow} className="space-y-5">
            <div className="text-center">
              <Illustration name="owl-cap" size={112} />
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight mt-2">
                Where should we send the full report?
              </h1>
              <p className="text-base text-muted-foreground mt-2 leading-relaxed">
                The marking is done. Tell us where to send it and the report opens right away.
              </p>
            </div>

            <div className="rounded-xl border border-border bg-card p-4 md:p-5 shadow-sm space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="pep-first-name">Your first name</Label>
                <Input
                  id="pep-first-name" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                  required maxLength={80} autoComplete="given-name" placeholder="Alicia"
                  className="h-12 text-base" data-testid="input-pep-first-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pep-email">Your email</Label>
                <Input
                  id="pep-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  required maxLength={200} autoComplete="email" inputMode="email" placeholder="you@example.com"
                  className="h-12 text-base" data-testid="input-pep-email"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pep-school">
                  Your child's school <span className="text-muted-foreground font-normal">(optional)</span>
                </Label>
                <Input
                  id="pep-school" value={school} onChange={(e) => setSchool(e.target.value)}
                  maxLength={160} placeholder="Mona Primary" className="h-12 text-base" data-testid="input-pep-school"
                />
              </div>

              <ul className="space-y-2 text-sm text-muted-foreground pt-1">
                {[
                  "The score out of 10 and the PEP band it falls in",
                  "The strongest subject and the two topics to fix first",
                  "What the PEP paper actually asks for in the weak subject",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>

              <Button
                type="submit" size="lg" className="w-full h-12 text-base font-semibold"
                disabled={busy || !firstName.trim() || !email.trim()} data-testid="button-pep-capture"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Show me the report
                {!busy && <ArrowRight className="w-4 h-4 ml-2" />}
              </Button>
              <p className="text-xs text-muted-foreground">
                We email the report and PEP tips for your child's grade. Every message has an unsubscribe link and one
                click stops them for good.
              </p>
            </div>
          </form>
        )}

        {/* ---------------------------------------------------------------- result */}
        {step === "result" && report && <PepResult report={report} onOfferClick={handleOfferClick} />}

      </main>
    </div>
  );
}
